import {Box, BoxProps, LinearProgress, Typography} from "@mui/material";
import {FC, useRef, useState, useContext} from "react";
import {makeStyles} from "tss-react/mui";
import {ExpandMore} from "@mui/icons-material";
import {palette} from "@/constants";
import {OutlineButton} from "@buildwitt/shapeshift-common/v1/buttons";
import {ModuleSummaryCard} from "@/components/module/ModuleSummaryCard";
import {ModuleSummaryCardSkeleton} from "@/components/module/ModuleSummaryCardSkeleton";
import {Link} from "react-router-dom";
import {useGetCourseCurriculum} from "@/app2/shared/data";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";
import {useSSTranslation} from "@/i18n/useSSTranslation";

interface IPropsCustom {
	course: {
		id: number;
		title: string;
		slug: string;
		progress: number;
		moduleCount: number;
		completedCount: number;
	};
}

export type IProps = BoxProps & IPropsCustom;

interface IStyleProps {
	isOpen: boolean
}

const useStyles = makeStyles<IStyleProps>()((
	theme,
	{isOpen}
) => ({
	myCoursesSlider: {
		padding: "20px",
		border: "1px solid " + palette.grey08,
		borderRadius: "16px",
		background: "#282828"
	},
	contentContainer: {

	},
	topContainer: {
		zIndex: 10,
		display: "flex",
		gap: "25px",
		flexDirection: "column"
	},
	titleContainer: {
		display: "flex",
		cursor: "pointer",
		gap: "5px",
		borderBottom: "1px solid " + palette.grey10
	},
	progressContainer: {
		padding: "0 0px",
		display: "flex",
		gap: "9px",
		"div:nth-of-type(1)": {
			flexGrow: 2,
			padding: "9px 0 0 0"
		},
		"span": {
			height: "6px"
		}
	},
	resumebtn: {
		width: "100%",
		color: theme.palette.common.white
	},
	progressPercentValue: {
		color: theme.palette.common.white
	},
	modulesParentContainer: {
	},
	moduleContainer: {
		paddingTop: "24px",
		display: "grid",
		gap: "20px"
	},
	icon: {
	},
	expandIcon: {
		fill: theme.palette.primary.main,
		fontSize: "24px",
		[theme.breakpoints.down("sm")]: {
			display: "none"
		},
		margin: "-2px 0 0",
		color: theme.palette.secondary.main,
		transform: `rotate(${isOpen ? "180deg" : "0deg"})`,
		transition: "transform 0.2s linear"
	},
	module: {
	},
	section: {
	},
	courseTitle: {
		paddingBottom: "18px",
		fontSize: "21px",
		fontWeight: 600,
		flexGrow: 2,
		overflow: "hidden",
		textWrap: "nowrap",
		textOverflow: "ellipsis",
		[theme.breakpoints.down("sm")]: {
			textAlign: "center"
		}

	},
	moduleSkeleton: {
		background: theme.palette.grey[200],
		height: "340px"
	}
}));

export const MyCoursesSlider: FC<IProps> = ({
	course, className, ...props
}) => {

	const [isOpen, setIsOpen] = useState(false);
	const {classes, cx} = useStyles({isOpen});
	const {t} = useSSTranslation();
	const accordianIconRef = useRef<SVGSVGElement>(null);
	const [{locale}] = useContext(LocaleContext);
	const curriculumParams = {locale: resolveLocale(locale)};
	const {data: curriculum, isLoading} = useGetCourseCurriculum(course.slug, curriculumParams, {enabled: isOpen});
	const resumeBtnContainerRef = useRef<HTMLDivElement>(null);

	const infoArray = [
		isOpen ? "isOpen" : ""
	];

	const handleOpenAccordian = (e: React.MouseEvent<HTMLDivElement>): void => {
		if(resumeBtnContainerRef.current && !resumeBtnContainerRef.current.contains(e.target as Node))
			setIsOpen(prev => !prev)
	};

	return (
		<Box
			className={cx(classes.myCoursesSlider, className, classes.section, infoArray)}
			{...props}
		>
			<Box className={classes.contentContainer}>
				<Box
					className={classes.topContainer}
					onClick={handleOpenAccordian}
				>
					<Box className={classes.titleContainer}>
						<Typography variant="h3" className={classes.courseTitle}>{course.title}</Typography>
						<ExpandMore
							ref={accordianIconRef}
							className={classes.expandIcon}
						/>
					</Box>
					<Box className={classes.progressContainer}>
						<Box>
							<LinearProgress variant="determinate" value={course.progress ?? 0}/>
						</Box>
						<Box className={classes.progressPercentValue}>
							<Typography>{course.progress}%</Typography>
						</Box>
					</Box>
					<Box ref={resumeBtnContainerRef} >
						<Link to={"/course/" + course.slug}>
							<OutlineButton
								className={classes.resumebtn}
								aria-label={t("page_progress_resume_button", "Resume")}
							>
								{t("page_progress_resume_button", "Resume")}
							</OutlineButton>
						</Link>
					</Box>
				</Box>
			</Box>
			<Box className={classes.modulesParentContainer} style={{height: isOpen ? "auto" : "0px"}}>
				{isOpen && <Box className={classes.moduleContainer}>
					{(curriculum === undefined || isLoading) && ([1,1,1,1])?.map((e: any, index: number) => <ModuleSummaryCardSkeleton key={index} />)}
					{curriculum?.items?.map((module: any) => (
						<Link to={`/course/${course.slug}/${module.type}/${module.slug}`} key={`${module.type}__${module.id}`}>
							<ModuleSummaryCard module={module} />
						</Link>
					))}
					{(curriculum?.items && curriculum?.items.length === 0) && <Box padding={3} sx={{textAlign: "center"}}><Typography>{t("page_progress_no_modules", "No modules found")}</Typography></Box>}
				</Box>}
			</Box>
		</Box>
	);
};

export default MyCoursesSlider;
